import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";

export const PaymentSuccess = () => {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("checking");
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!sessionId) { setStatus("unknown"); return; }
        let tries = 0;
        const poll = async () => {
            tries++;
            try {
                const r = await api.get(`/payments/status/${sessionId}`);
                setData(r.data);
                if (r.data.payment_status === "paid" && r.data.blessing) { setStatus("paid"); return; }
                if (tries < 12) setTimeout(poll, 2000);
                else setStatus("pending");
            } catch { setStatus("error"); }
        };
        poll();
    }, [sessionId]);

    const nameGreeting = data?.first_name ? `Thank you, ${data.first_name}.` : "Thank you.";
    const gift = data?.amount ? `$${data.amount.toFixed(2)}${data.recurring ? " each month" : ""}` : null;

    return (
        <div className="max-w-2xl mx-auto px-6 py-24" data-testid="payment-success">
            <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full border border-liturgy flex items-center justify-center animate-flicker" style={{background: 'hsl(var(--liturgy) / 0.08)'}}>
                    <span className="font-serif text-3xl accent-liturgy">†</span>
                </div>
                <div className="mt-6 small-caps text-xs text-muted-foreground">Free-will offering</div>
                <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">{nameGreeting}</h1>
            </div>

            <div className="mt-12 missal-card p-8 md:p-12">
                {status === "checking" && (
                    <div className="text-center animate-pulse" data-testid="payment-status-checking">
                        <div className="small-caps text-xs accent-liturgy mb-2">Confirming your offering</div>
                        <p className="text-sm text-muted-foreground italic">Please wait a moment. A personal note is being prepared for you.</p>
                    </div>
                )}
                {status === "paid" && data?.blessing && (
                    <div className="text-center" data-testid="payment-blessing">
                        {gift && <div className="small-caps text-[11px] accent-liturgy mb-4">Your gift of {gift} · received</div>}
                        <blockquote className="border-l-2 border-liturgy pl-6 py-2 text-left font-body text-lg leading-relaxed italic">
                            {data.blessing}
                        </blockquote>
                        <div className="mt-8 text-sm text-muted-foreground">
                            — Written personally for you by the Hearts with Him ministry
                        </div>
                    </div>
                )}
                {status === "pending" && (
                    <div className="text-center" data-testid="payment-pending">
                        <p className="text-sm text-muted-foreground italic">Your gift is being processed. We'll email you as soon as it lands. You may return home in the meantime.</p>
                    </div>
                )}
                {status === "error" && (
                    <div className="text-center" data-testid="payment-error">
                        <p className="text-sm text-muted-foreground italic">We could not confirm — but if your card was charged, we have it. Please write to us and we'll respond personally.</p>
                    </div>
                )}
            </div>

            <div className="mt-10 text-center flex items-center justify-center gap-3 flex-wrap">
                <Link to="/" className="px-6 py-3 bg-liturgy text-white small-caps text-xs" data-testid="back-home">Return home</Link>
                <Link to="/wall" className="px-6 py-3 border border-liturgy accent-liturgy small-caps text-xs" data-testid="visit-wall">Leave an intention</Link>
            </div>
        </div>
    );
};

export const PaymentCancel = () => (
    <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="payment-cancel">
        <h1 className="font-serif text-4xl">No offering made.</h1>
        <p className="mt-6 text-muted-foreground italic">All is well. Come again whenever the Spirit moves you.</p>
        <Link to="/" className="mt-10 inline-block px-6 py-3 border border-liturgy accent-liturgy small-caps">Return home</Link>
    </div>
);
